import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import {
  getGameNav,
  isLeaveTrapState,
  MYM_GUARD_KEY,
  MYM_NAV_KEY,
} from "../../utils/mymGameHistory";

const shouldSkipGuard = (pathname, search) => {
  if (typeof window !== "undefined" && window.top !== window) return true;
  const path = (pathname || "").toLowerCase();
  if (path.startsWith("/admin") || path === "/sp-login" || path === "/adminlogin") {
    return true;
  }
  const params = new URLSearchParams(search || "");
  if (params.has("forceId")) return true;
  if (["1", "true", "yes"].includes((params.get("gigmode") || "").toLowerCase())) {
    return true;
  }
  return false;
};

/**
 * Keep a dummy history entry on top of MYM so Back can be intercepted when it
 * would leave the site. Never arm while a game UI stack is the current entry.
 */
export const armBrowserBackGuard = () => {
  if (typeof window === "undefined" || window.top !== window) return;
  if (shouldSkipGuard(window.location.pathname, window.location.search)) return;
  if (getGameNav()) return;
  if (isLeaveTrapState()) return;
  const prev = { ...(window.history.state || {}) };
  delete prev[MYM_NAV_KEY];
  prev[MYM_GUARD_KEY] = true;
  window.history.pushState(prev, "", window.location.href);
};

/**
 * Site-wide browser-back confirm when Back would leave the site.
 * Game modal Home → Mode → Game entries are handled by mymGameHistory; this
 * guard must not re-arm on those pops or it would trap Back inside the game.
 */
const BrowserBackGuard = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const allowLeaveRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || window.top !== window) return undefined;

    armBrowserBackGuard();

    const onPopState = (event) => {
      if (allowLeaveRef.current) {
        allowLeaveRef.current = false;
        return;
      }
      if (shouldSkipGuard(window.location.pathname, window.location.search)) return;
      if (getGameNav(event.state)) return;
      // Landed on the trap after closing a game (or after an in-game Back). Stay.
      if (isLeaveTrapState(event.state)) return;
      armBrowserBackGuard();
      setOpen(true);
    };

    const onArm = () => armBrowserBackGuard();

    window.addEventListener("popstate", onPopState);
    window.addEventListener("browser-back-arm", onArm);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("browser-back-arm", onArm);
    };
  }, [location.pathname, location.search]);

  const stay = () => setOpen(false);

  const leave = () => {
    setOpen(false);
    const consumed = !window.dispatchEvent(
      new CustomEvent("browser-back-leave", { cancelable: true }),
    );
    if (consumed) return;
    allowLeaveRef.current = true;
    window.history.go(-2);
  };

  return (
    <Dialog
      open={open}
      onClose={stay}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 20000 }}
      PaperProps={{
        sx: {
          background:
            "linear-gradient(135deg, rgba(20,0,40,0.98) 0%, rgba(40,0,60,0.98) 100%)",
          backdropFilter: "blur(20px)",
          border: "2px solid rgba(255,0,255,0.4)",
          borderRadius: "16px",
          boxShadow:
            "0 20px 60px rgba(255,0,255,0.5), 0 0 40px rgba(0,255,255,0.3)",
        },
      }}
    >
      <DialogTitle sx={{ color: "#fff", textAlign: "center", pt: 3, pb: 1 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            mx: "auto",
            mb: 1.5,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,0,255,0.18)",
            color: "#ff6b9d",
          }}
        >
          <LocalFireDepartmentIcon sx={{ fontSize: 30 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.35rem" }}>
          Bailing already?
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ textAlign: "center", pb: 2, px: 3 }}>
        <Typography
          variant="body1"
          sx={{ color: "rgba(255,255,255,0.82)", lineHeight: 1.6 }}
        >
          Walk away now and that streak can go up in smoke. You might lose your
          progress — no take-backs, no mercy. You sure you wanna bounce?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3, px: 3 }}>
        <Button
          onClick={stay}
          variant="outlined"
          sx={{
            color: "#fff",
            borderColor: "rgba(255,255,255,0.3)",
            borderRadius: "25px",
            px: 4,
            py: 1,
            textTransform: "none",
            fontWeight: 600,
            "&:hover": {
              borderColor: "rgba(255,255,255,0.6)",
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          Keep playing
        </Button>
        <Button
          onClick={leave}
          variant="contained"
          sx={{
            background: "linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)",
            color: "white",
            borderRadius: "25px",
            px: 4,
            py: 1,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "0 4px 20px rgba(255,0,255,0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)",
              boxShadow: "0 6px 28px rgba(255,0,255,0.4)",
            },
          }}
        >
          Yeah, I&apos;m out
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrowserBackGuard;
